import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { Badge, Empty, Field, Info, SectionTitle, Table } from "./_ui";
import {
  CONTRACT_TYPES,
  ESTABLISHMENT_TYPES,
  FREQUENCIES,
  calcPriceLocal,
  customerWhatsAppHref,
  fmtDateTime,
  formatCHF,
  serviceTypeLabel,
} from "../../lib/adminUtils";
import {
  X, Save, Trash2, Loader2, Copy, MessageCircle, Mail, Plus, Check,
} from "lucide-react";

export default function ClientsView({ items, settings, reload, openId, clearOpenId }) {
  const [selectedId, setSelectedId] = useState(null);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (openId) {
      setSelectedId(openId);
      clearOpenId?.();
    }
  }, [openId, clearOpenId]);

  return (
    <div data-testid="clients-view">
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-slate-600">
          {items.length} client{items.length > 1 ? "s" : ""}.
        </p>
        <button
          onClick={() => setCreating(true)}
          className="btn-primary !text-xs !py-2 !px-3"
          data-testid="client-add-btn"
        >
          <Plus size={14} /> Nouveau client
        </button>
      </div>

      {items.length === 0 ? (
        <Empty label="Aucun client. Convertissez une demande ou créez-en un manuellement." />
      ) : (
        <Table
          headers={["Établissement", "Contact", "Ville", "Surface", "Service", "Statut", ""]}
          testid="clients-list"
        >
          {items.map((c) => {
            const ct = c.contract;
            return (
              <tr key={c.id} className="border-t border-slate-100 hover:bg-slate-50">
                <td className="p-3 font-medium text-slate-900">{c.establishment}</td>
                <td className="p-3 text-slate-700">
                  {c.contactName}
                  <br />
                  <span className="text-xs text-slate-500">{c.phone}</span>
                </td>
                <td className="p-3 text-slate-700">{c.city}</td>
                <td className="p-3 text-slate-700">{c.surface ? `${c.surface} m²` : "—"}</td>
                <td className="p-3 text-xs text-slate-600">
                  {ct ? (
                    <span>
                      {serviceTypeLabel(ct.type)}
                      <br />
                      <span className="text-slate-400">{formatCHF(ct.finalPrice)}</span>
                    </span>
                  ) : (
                    "—"
                  )}
                </td>
                <td className="p-3">
                  {ct?.active ? (
                    <span className="inline-block text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded bg-emerald-100 text-emerald-800">
                      Actif
                    </span>
                  ) : (
                    <span className="text-xs text-slate-500">Sans service actif</span>
                  )}
                </td>
                <td className="p-3 text-right">
                  <button
                    onClick={() => setSelectedId(c.id)}
                    className="text-xs text-[#016C36] hover:underline font-semibold"
                    data-testid={`client-open-${c.id}`}
                  >
                    Ouvrir
                  </button>
                </td>
              </tr>
            );
          })}
        </Table>
      )}

      {creating && (
        <NewCustomerModal
          onClose={() => setCreating(false)}
          onSaved={(id) => {
            setCreating(false);
            reload();
            setSelectedId(id);
          }}
        />
      )}

      {selectedId && (
        <CustomerDetail
          id={selectedId}
          settings={settings}
          onClose={() => setSelectedId(null)}
          reloadList={reload}
        />
      )}
    </div>
  );
}

function NewCustomerModal({ onClose, onSaved }) {
  const [form, setForm] = useState({
    establishment: "",
    contactName: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    establishmentType: "",
    surface: "",
  });
  const [saving, setSaving] = useState(false);
  const upd = (k, v) => setForm({ ...form, [k]: v });

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, surface: form.surface ? Number(form.surface) : null };
      const r = await api.post("/admin/customers", payload);
      onSaved(r.data.id);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-950/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white max-w-xl w-full rounded-md p-6" onClick={(e) => e.stopPropagation()} data-testid="new-client-modal">
        <h3 className="font-display font-bold text-lg mb-4">Nouveau client</h3>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <Field label="Établissement">
              <input className="field" value={form.establishment} onChange={(e) => upd("establishment", e.target.value)} data-testid="new-client-est" />
            </Field>
          </div>
          <Field label="Contact"><input className="field" value={form.contactName} onChange={(e) => upd("contactName", e.target.value)} /></Field>
          <Field label="Téléphone"><input className="field" value={form.phone} onChange={(e) => upd("phone", e.target.value)} /></Field>
          <Field label="Email"><input className="field" type="email" value={form.email} onChange={(e) => upd("email", e.target.value)} /></Field>
          <Field label="Ville"><input className="field" value={form.city} onChange={(e) => upd("city", e.target.value)} /></Field>
          <div className="col-span-2">
            <Field label="Adresse"><input className="field" value={form.address} onChange={(e) => upd("address", e.target.value)} /></Field>
          </div>
          <Field label="Type d'établissement">
            <select className="field" value={form.establishmentType} onChange={(e) => upd("establishmentType", e.target.value)}>
              <option value="">— Type —</option>
              {ESTABLISHMENT_TYPES.map((t) => (<option key={t} value={t}>{t}</option>))}
            </select>
          </Field>
          <Field label="Surface (m²)"><input className="field" type="number" value={form.surface} onChange={(e) => upd("surface", e.target.value)} /></Field>
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} className="btn-secondary !text-xs">Annuler</button>
          <button onClick={save} disabled={saving || !form.establishment || !form.email} className="btn-primary !text-xs" data-testid="new-client-save">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Créer
          </button>
        </div>
      </div>
    </div>
  );
}

function CustomerDetail({ id, settings, onClose, reloadList }) {
  const [cust, setCust] = useState(null);
  const [tab, setTab] = useState("general");

  const load = async () => {
    const r = await api.get(`/admin/customers/${id}`);
    setCust(r.data);
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (!cust) {
    return (
      <div className="fixed inset-0 z-40 bg-slate-950/50 flex justify-end" onClick={onClose}>
        <div className="w-full max-w-2xl bg-white h-full p-6"><Loader2 className="animate-spin" /></div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-40 bg-slate-950/50 flex justify-end" onClick={onClose} data-testid="client-drawer">
      <div className="w-full max-w-2xl bg-white h-full overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-slate-950 text-white px-6 py-4 flex justify-between items-center z-10">
          <div>
            <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Client</p>
            <h2 className="font-display font-bold text-lg">{cust.establishment}</h2>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white"><X size={20} /></button>
        </div>

        <div className="flex gap-1 border-b border-slate-200 px-6 pt-3 bg-slate-50 sticky top-[68px] z-10">
          {[
            ["general", "Général"],
            ["contract", "Service"],
            ["payments", "Paiements"],
          ].map(([k, l]) => (
            <button
              key={k}
              onClick={() => setTab(k)}
              className={`px-3 py-2 text-xs font-semibold uppercase tracking-wider ${
                tab === k ? "text-[#016C36] border-b-2 border-[#016C36]" : "text-slate-500"
              }`}
              data-testid={`client-tab-${k}`}
            >
              {l}
            </button>
          ))}
        </div>

        <div className="p-6 space-y-5">
          {tab === "general" && <GeneralTab cust={cust} onChange={load} reloadList={reloadList} onClose={onClose} />}
          {tab === "contract" && <ContractTab cust={cust} settings={settings} reload={() => { load(); reloadList(); }} />}
          {tab === "payments" && <PaymentsTab cust={cust} reload={load} />}
        </div>
      </div>
    </div>
  );
}

// ── General tab ────────────────────────────────────────────────────────────
function GeneralTab({ cust, onChange, reloadList, onClose }) {
  const [form, setForm] = useState({
    establishment: cust.establishment || "",
    contactName: cust.contactName || "",
    phone: cust.phone || "",
    email: cust.email || "",
    address: cust.address || "",
    city: cust.city || "",
    establishmentType: cust.establishmentType || "",
    surface: cust.surface || "",
    notes_text: cust.notes_text || "",
  });
  const [saving, setSaving] = useState(false);
  const upd = (k, v) => setForm({ ...form, [k]: v });

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, surface: form.surface ? Number(form.surface) : null };
      await api.patch(`/admin/customers/${cust.id}`, payload);
      onChange();
      reloadList();
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("Supprimer ce client ?")) return;
    await api.delete(`/admin/customers/${cust.id}`);
    reloadList();
    onClose();
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap gap-2">
        {cust.phone && (
          <a href={`tel:${cust.phone}`} className="btn-secondary !text-xs"><span>Appeler</span></a>
        )}
        {cust.phone && (
          <a
            href={customerWhatsAppHref(cust, `Bonjour ${cust.contactName}, c'est SAFEMAT.`)}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs bg-[#25D366] text-white rounded font-semibold"
          >
            <MessageCircle size={12} /> WhatsApp
          </a>
        )}
        {cust.email && (
          <a href={`mailto:${cust.email}`} className="btn-secondary !text-xs">
            <Mail size={12} /> Email
          </a>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2"><Field label="Établissement"><input className="field" value={form.establishment} onChange={(e) => upd("establishment", e.target.value)} /></Field></div>
        <Field label="Contact"><input className="field" value={form.contactName} onChange={(e) => upd("contactName", e.target.value)} /></Field>
        <Field label="Téléphone"><input className="field" value={form.phone} onChange={(e) => upd("phone", e.target.value)} /></Field>
        <Field label="Email"><input className="field" value={form.email} onChange={(e) => upd("email", e.target.value)} /></Field>
        <Field label="Ville"><input className="field" value={form.city} onChange={(e) => upd("city", e.target.value)} /></Field>
        <div className="col-span-2"><Field label="Adresse"><input className="field" value={form.address} onChange={(e) => upd("address", e.target.value)} /></Field></div>
        <Field label="Type">
          <select className="field" value={form.establishmentType} onChange={(e) => upd("establishmentType", e.target.value)}>
            <option value="">—</option>
            {ESTABLISHMENT_TYPES.map((t) => (<option key={t} value={t}>{t}</option>))}
          </select>
        </Field>
        <Field label="Surface confirmée (m²)"><input className="field" type="number" value={form.surface} onChange={(e) => upd("surface", e.target.value)} data-testid="client-surface" /></Field>
        <div className="col-span-2"><Field label="Notes"><textarea className="field" rows={3} value={form.notes_text} onChange={(e) => upd("notes_text", e.target.value)} /></Field></div>
      </div>

      <div className="flex justify-between border-t border-slate-200 pt-4">
        <button onClick={remove} className="btn-secondary !text-xs text-[#016C36]"><Trash2 size={12} /> Supprimer</button>
        <button onClick={save} disabled={saving} className="btn-primary !text-xs" data-testid="client-save">
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Enregistrer
        </button>
      </div>
    </div>
  );
}

// ── Contract tab ───────────────────────────────────────────────────────────
function ContractTab({ cust, settings, reload }) {
  const existing = cust.contract;
  const existingType = ["one_time", "one-time", "oneoff"].includes(existing?.type) ? "one_time" : "recurring";
  const [form, setForm] = useState(
    existing
      ? {
          type: existingType,
          active: existing.active ?? true,
          frequency: "custom",
          frequencyPerMonth: existing.frequencyPerMonth || existing.customPerMonth || 2,
          overridePrice: existing.overridePrice ?? "",
          applyFirstPromo: !!existing.applyFirstPromo,
        }
      : {
          type: "recurring",
          active: true,
          frequency: "custom",
          frequencyPerMonth: settings?.rules?.defaultFrequencyPerMonth || 2,
          overridePrice: "",
          applyFirstPromo: false,
        }
  );
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);

  const upd = (k, v) => setForm({ ...form, [k]: v });

  const calc = calcPriceLocal(cust.surface, form.type, settings, form.applyFirstPromo);
  const finalPrice = form.overridePrice !== "" ? Number(form.overridePrice) : calc.total;

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        overridePrice: form.overridePrice === "" ? null : Number(form.overridePrice),
        frequencyPerMonth: Number(form.frequencyPerMonth) || 2,
        customPerMonth: Number(form.frequencyPerMonth) || 2,
        frequency: "custom",
      };
      const r = await api.put(`/admin/customers/${cust.id}/contract`, payload);
      setResult(r.data);
      reload();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="contract-tab">
      {!cust.surface && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded p-3 text-xs">
          Renseignez d'abord la <strong>surface confirmée</strong> (onglet Général) pour activer le calculateur.
        </div>
      )}

      {/* Calculator */}
      <div className="bg-slate-50 border border-slate-200 rounded-md p-4">
        <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2">
          Référence tarifaire interne
        </p>
        <div className="text-sm text-slate-700 space-y-1">
          <p>Surface : <strong>{cust.surface || 0} m²</strong></p>
          <p>Base estimée : {formatCHF(calc.base)}</p>
          {calc.promoApplied && (<p className="text-emerald-700">Promo 1ère intervention : − {formatCHF(calc.promoAmount)}</p>)}
          <p className="font-display font-bold text-lg text-slate-900 mt-2">
            Calculé : {formatCHF(calc.total)}
          </p>
          {form.overridePrice !== "" && (
            <p className="font-display font-bold text-lg text-[#016C36]" data-testid="contract-final">
              Override appliqué : {formatCHF(finalPrice)}
            </p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <Field label="Type de service">
          <select className="field" value={form.type} onChange={(e) => upd("type", e.target.value)} data-testid="contract-type">
            {CONTRACT_TYPES.map((t) => (<option key={t.key} value={t.key}>{t.label}</option>))}
          </select>
        </Field>
        <Field label="Actif">
          <select className="field" value={form.active ? "yes" : "no"} onChange={(e) => upd("active", e.target.value === "yes")}>
            <option value="yes">Oui</option>
            <option value="no">Non</option>
          </select>
        </Field>
        {form.type === "recurring" ? (
          <Field label="Fréquence par mois">
            <select className="field" value={form.frequencyPerMonth} onChange={(e) => upd("frequencyPerMonth", e.target.value)} data-testid="contract-freq">
              {FREQUENCIES.map((f) => (<option key={f.key} value={f.key}>{f.label}</option>))}
            </select>
          </Field>
        ) : (
          <Field label="Fréquence">
            <input className="field" value="Intervention ponctuelle" disabled />
          </Field>
        )}

        <Field label="Prix convenu (CHF)" hint="Laissez vide pour utiliser le prix calculé.">
          <input className="field" type="number" step="0.01" value={form.overridePrice} onChange={(e) => upd("overridePrice", e.target.value)} data-testid="contract-override" />
        </Field>
        <Field label="Promo 1ère intervention">
          <select className="field" value={form.applyFirstPromo ? "yes" : "no"} onChange={(e) => upd("applyFirstPromo", e.target.value === "yes")}>
            <option value="no">Non</option>
            <option value="yes">Oui</option>
          </select>
        </Field>
      </div>

      <div className="flex flex-wrap gap-2 justify-end border-t border-slate-200 pt-4">
        <button onClick={save} disabled={saving || !cust.surface} className="btn-primary !text-xs" data-testid="contract-save">
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
          Enregistrer le service
        </button>
      </div>

      {result?.contract && (
        <div className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded p-2">
          <Check size={12} className="inline mr-1" />
          Service enregistré.
        </div>
      )}
    </div>
  );
}

// ── Payments tab ───────────────────────────────────────────────────────────
function PaymentsTab({ cust, reload }) {
  const [form, setForm] = useState({
    serviceType: ["one_time", "one-time", "oneoff"].includes(cust.contract?.type) ? "one_time" : "recurring",
    amount: cust.contract?.finalPrice || "",
    frequencyPerMonth: cust.contract?.frequencyPerMonth || 2,
    internalNote: "",
    description: `Désinfection vapeur professionnelle — ${cust.establishment}`,
  });
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState("");

  const create = async () => {
    setCreating(true);
    setError("");
    try {
      await api.post("/admin/payment-links", {
        customerId: cust.id,
        serviceType: form.serviceType,
        amount: Number(form.amount),
        description: form.description,
        frequencyPerMonth: form.serviceType === "recurring" ? Number(form.frequencyPerMonth) : null,
        internalNote: form.internalNote,
      });
      reload();
    } catch (e) {
      setError(e?.response?.data?.detail || "Erreur");
    } finally {
      setCreating(false);
    }
  };

  const markPaid = async (pl) => {
    await api.patch(`/admin/payment-links/${pl.id}`, { status: "manual_paid" });
    reload();
  };

  return (
    <div className="space-y-5" data-testid="payments-tab">
      <div className="bg-slate-50 border border-slate-200 rounded-md p-4 grid sm:grid-cols-3 gap-3">
        <Field label="Type de service">
          <select className="field" value={form.serviceType} onChange={(e) => setForm({ ...form, serviceType: e.target.value })}>
            {CONTRACT_TYPES.map((t) => (<option key={t.key} value={t.key}>{t.label}</option>))}
          </select>
        </Field>
        <Field label="Montant (CHF)">
          <input className="field" type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="pl-amount" />
        </Field>
        {form.serviceType === "recurring" && (
          <Field label="Fréquence / mois">
            <input className="field" type="number" min="1" step="1" value={form.frequencyPerMonth} onChange={(e) => setForm({ ...form, frequencyPerMonth: e.target.value })} />
          </Field>
        )}
        <div className="sm:col-span-3">
          <Field label="Description (visible sur Stripe)">
            <input className="field" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </Field>
        </div>
        <div className="sm:col-span-3">
          <Field label="Note interne">
            <textarea className="field" rows={2} value={form.internalNote} onChange={(e) => setForm({ ...form, internalNote: e.target.value })} />
          </Field>
        </div>
        {error && <p className="sm:col-span-3 text-xs text-[#016C36]">{error}</p>}
        <div className="sm:col-span-3">
          <button onClick={create} disabled={creating || !form.amount} className="btn-primary !text-xs" data-testid="pl-create">
            {creating ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Générer un lien Stripe
          </button>
        </div>
      </div>

      {(cust.paymentLinks || []).length === 0 ? (
        <Empty label="Aucun lien de paiement pour ce client." />
      ) : (
        <div className="space-y-2">
          {(cust.paymentLinks || []).map((p) => (
            <div key={p.id} className="border border-slate-200 rounded-md p-3 bg-white" data-testid={`pl-${p.id}`}>
              <div className="flex justify-between gap-3">
                <div>
                  <p className="font-medium text-slate-900">{p.description}</p>
                  <p className="text-xs text-slate-500">{fmtDateTime(p.createdAt)} · {serviceTypeLabel(p.serviceType || p.productType)}</p>
                </div>
                <div className="text-right">
                  <p className="font-display font-bold text-lg">{formatCHF(p.amount)}</p>
                  <Badge status={p.status} />
                </div>
              </div>
              <div className="mt-2 flex flex-wrap gap-1.5">
                <CopyBtn value={p.checkoutUrl || p.url} />
                <a
                  href={customerWhatsAppHref(cust, `Bonjour ${cust.contactName},\n\nVoici votre lien de paiement SafeMat :\n\n${p.checkoutUrl || p.url}\n\nMerci pour votre confiance.`)}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-[#25D366] text-white rounded"
                >
                  <MessageCircle size={11} /> WhatsApp
                </a>
                <a
                  href={`mailto:${cust.email}?subject=${encodeURIComponent("Votre lien de paiement SafeMat")}&body=${encodeURIComponent(`Bonjour ${cust.contactName},\n\nVeuillez trouver ci-dessous votre lien de paiement sécurisé :\n\n${p.checkoutUrl || p.url}\n\nMerci pour votre confiance.`)}`}
                  className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-slate-200 rounded hover:bg-slate-50"
                >
                  <Mail size={11} /> Email
                </a>
                <a
                  href={p.checkoutUrl || p.url}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-slate-200 rounded hover:bg-slate-50"
                >
                  Ouvrir Checkout
                </a>
                {p.status !== "paid" && p.status !== "manual_paid" && (
                  <button onClick={() => markPaid(p)} className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-emerald-200 text-emerald-700 rounded hover:bg-emerald-50" data-testid={`pl-markpaid-${p.id}`}>
                    <Check size={11} /> Marquer payé
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function CopyBtn({ value }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      onClick={() => {
        navigator.clipboard.writeText(value);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      }}
      className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-slate-200 rounded hover:bg-slate-50"
    >
      <Copy size={11} /> {copied ? "OK" : "Copier"}
    </button>
  );
}
